import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  Textarea,
  useDisclosure
} from '@chakra-ui/react';
import { AddIcon, CloseIcon } from '@chakra-ui/icons';
import CreatableSelect from 'react-select/creatable';
import Select from 'react-select';
import type { SingleValue, StylesConfig } from 'react-select';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef, ColumnState, GridOptions, ICellRendererParams } from 'ag-grid-community';
import { getStoreData, getStoreId, getUserEmail, getUserId } from '@/redux/app-selectors';
import type { RootState } from '@/redux/store';
import {
  callToDeleteEcommerceStatusMapping,
  callToFetchEcommerceStatusList,
  callToSaveEcommerceStatusMapping,
  setEcommerceStatusMappingDeleteError,
  setEcommerceStatusMappingSaveError
} from '@/storeAdmin/ducks/slice';
import { agGridIconsConfig } from '@/explore/ffl-detail/utils.mjs';
import EcommerceStatusActionMappingDataSource, {
  ECOMMERCE_STATUS_MAPPING_CACHE_BLOCK_SIZE,
  type EcommerceStatusMappingRow
} from '@/storeAdmin/ducks/ecommerceStatusActionMappingDatasource';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

function EditMappingIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden>
      <path
        d="M9 2.25H3.75C3.35218 2.25 2.97064 2.40804 2.68934 2.68934C2.40804 2.97064 2.25 3.35218 2.25 3.75V14.25C2.25 14.6478 2.40804 15.0294 2.68934 15.3107C2.97064 15.592 3.35218 15.75 3.75 15.75H14.25C14.6478 15.75 15.0294 15.592 15.3107 15.3107C15.592 15.0294 15.75 14.6478 15.75 14.25V9"
        stroke="#0C1E46"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M13.7813 1.96892C14.0797 1.67055 14.4844 1.50293 14.9063 1.50293C15.3283 1.50293 15.733 1.67055 16.0313 1.96892C16.3297 2.26729 16.4973 2.67196 16.4973 3.09392C16.4973 3.51588 16.3297 3.92055 16.0313 4.21892L9.27157 10.9794C9.09348 11.1574 8.87347 11.2876 8.63182 11.3582L6.47707 11.9882C6.41253 12.007 6.34412 12.0081 6.279 11.9914C6.21388 11.9748 6.15444 11.9409 6.10691 11.8933C6.05937 11.8458 6.02549 11.7864 6.0088 11.7212C5.99212 11.6561 5.99325 11.5877 6.01207 11.5232L6.64207 9.36842C6.71297 9.12696 6.84347 8.90721 7.02157 8.72942L13.7813 1.96892Z"
        stroke="#0C1E46"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function DeleteMappingIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden>
      <path
        d="M2.25 4.5H15.75"
        stroke="#0C1E46"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14.25 4.5V15C14.25 15.75 13.5 16.5 12.75 16.5H5.25C4.5 16.5 3.75 15.75 3.75 15V4.5"
        stroke="#0C1E46"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6 4.5V3C6 2.25 6.75 1.5 7.5 1.5H10.5C11.25 1.5 12 2.25 12 3V4.5"
        stroke="#0C1E46"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M7.5 8.25V12.75"
        stroke="#0C1E46"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10.5 8.25V12.75"
        stroke="#0C1E46"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const actionIconButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '4px',
  margin: 0,
  background: 'transparent',
  border: 'none',
  outline: 'none',
  boxShadow: 'none',
  cursor: 'pointer',
  lineHeight: 0
};

type EcommerceStatusRow = EcommerceStatusMappingRow;

type StatusOption = { label: string; value: string };

const TRANSFER_ACTION_OPTIONS: StatusOption[] = [
  { label: 'Archive & Notify Dealer', value: 'archive_and_notify_dealer' },
  { label: 'Archive Transfer', value: 'archive' },
  { label: 'Unarchive Transfer', value: 'unarchive' }
];

const ACTION_DISPLAY: Record<string, string> = {
  archive: 'Archive Transfer',
  archive_and_notify_dealer: 'Archive & Notify Dealer',
  unarchive: 'Unarchive Transfer'
};

const DEFAULT_ECOMMERCE_STATUSES = ['Cancelled', 'Refunded', 'On Hold', 'Processing'] as const;

/** Map user input to server/API spelling when it matches case-insensitively. */
function canonicalEcommerceStatus(value: string, apiStatuses: string[]): string {
  const t = String(value ?? '').trim();
  if (!t) return '';
  const apiMatch = apiStatuses.find((s) => s.toLowerCase() === t.toLowerCase());
  if (apiMatch !== undefined) return apiMatch;
  const legacyMatch = DEFAULT_ECOMMERCE_STATUSES.find((s) => s.toLowerCase() === t.toLowerCase());
  return legacyMatch ?? t;
}

function actionDisplayLabel(actionCode: string): string {
  return ACTION_DISPLAY[actionCode] ?? actionCode;
}

function toTitleCaseWords(value: string): string {
  const normalized = String(value ?? '').trim();
  if (!normalized) return '';
  return normalized.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
}

/** Shared react-select styles for modal comboboxes (matches E‑Commerce Status look). */
const mappingModalSelectStyles: StylesConfig<StatusOption, false> = {
  control: (base, state) => ({
    ...base,
    minHeight: '48px',
    borderRadius: '6px',
    borderColor: '#D0D5DD',
    fontFamily: 'Lato, sans-serif',
    fontSize: '14px',
    boxShadow: 'none',
    '&:hover': { borderColor: '#D0D5DD' },
    ...(state.isFocused ? { borderColor: '#98A2B3', boxShadow: 'none' } : {})
  }),
  valueContainer: (base) => ({ ...base, padding: '2px 10px' }),
  placeholder: (base) => ({ ...base, color: '#667085', fontSize: '14px' }),
  singleValue: (base) => ({ ...base, fontSize: '14px', color: '#101828' }),
  input: (base) => ({ ...base, fontSize: '14px', color: '#101828' }),
  indicatorsContainer: (base) => ({ ...base, paddingRight: '4px' }),
  indicatorSeparator: (base) => ({
    ...base,
    backgroundColor: '#D0D5DD',
    marginTop: '10px',
    marginBottom: '10px'
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: '#667085',
    padding: '8px',
    '&:hover': { color: '#101828' }
  }),
  menu: (base) => ({
    ...base,
    zIndex: 2000,
    marginTop: '4px',
    borderRadius: '6px',
    border: '1px solid #EAECF0',
    boxShadow:
      '0px 4px 6px -2px rgba(16, 24, 40, 0.08), 0px 12px 16px -4px rgba(16, 24, 40, 0.14)',
    overflow: 'hidden',
    backgroundColor: '#fff'
  }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
  menuList: (base) => ({ ...base, padding: '4px' }),
  option: (base, state) => ({
    ...base,
    fontFamily: 'Lato, sans-serif',
    fontSize: '14px',
    padding: '10px 12px',
    borderRadius: '4px',
    cursor: 'pointer',
    backgroundColor: state.isFocused ? '#F2F4F7' : '#fff',
    color: '#101828'
  })
};

type TransferActionStyle = {
  bg: string;
  color: string;
  alignItems: 'flex-start' | 'center';
};

const transferActionStyles: Record<string, TransferActionStyle> = {
  archive: {
    bg: '#F8D7DA',
    color: '#721C24',
    alignItems: 'flex-start'
  },
  archive_and_notify_dealer: {
    bg: '#FFF3CD',
    color: '#856404',
    alignItems: 'center'
  },
  unarchive: {
    bg: '#D4EDDA',
    color: '#155724',
    alignItems: 'center'
  }
};

type MappingModalMode = 'add' | 'edit';

function rowKey(r: EcommerceStatusRow): string {
  if (r.id != null && String(r.id).trim() !== '') {
    return String(r.id);
  }
  return `${r.ecommerceStatus}\u0000${r.action}\u0000${r.description}`;
}

export default function EcommerceStatusMapping() {
  const dispatch = useDispatch();
  const isSavingEcommerceStatusMapping = useSelector(
    (state: RootState) => state.store.isSavingEcommerceStatusMapping
  );
  const ecommerceStatusMappingSaveError = useSelector(
    (state: RootState) => state.store.ecommerceStatusMappingSaveError
  );
  const isDeletingEcommerceStatusMapping = useSelector(
    (state: RootState) => state.store.isDeletingEcommerceStatusMapping
  );
  const ecommerceStatusMappingDeleteError = useSelector(
    (state: RootState) => state.store.ecommerceStatusMappingDeleteError
  );
  const ecommerceStatusList = useSelector((state: RootState) => state.store.ecommerceStatusList);
  const isLoadingEcommerceStatusList = useSelector(
    (state: RootState) => state.store.isLoadingEcommerceStatusList
  );
  const ecommerceStatusListLoadError = useSelector(
    (state: RootState) => state.store.ecommerceStatusListLoadError
  );
  const saveSubmittedRef = useRef(false);
  const saveRequestSawInProgressRef = useRef(false);
  /** Row id from the row that opened the edit modal (avoids losing `id` vs React state timing). */
  const editMappingIdRef = useRef<string | number | null>(null);
  const deleteMappingIdRef = useRef<string | number | null>(null);
  const deleteSubmittedRef = useRef(false);
  const deleteRequestSawInProgressRef = useRef(false);
  const [listLoading, setListLoading] = useState(false);
  const [rowVersion, setRowVersion] = useState(0);
  const storeIdFromApp = getStoreId();
  const storeData = getStoreData();
  const effectiveStoreId = (storeIdFromApp || storeData?.store_id || '').trim();
  const dataSource = useMemo(() => new EcommerceStatusActionMappingDataSource(), []);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isDeleteConfirmOpen,
    onOpen: onDeleteConfirmOpen,
    onClose: onDeleteConfirmClose
  } = useDisclosure();
  const [deleteRowIndex, setDeleteRowIndex] = useState<number | null>(null);
  const [modalMode, setModalMode] = useState<MappingModalMode>('add');
  const [editingMappingId, setEditingMappingId] = useState<string | null>(null);
  const [ecommerceStatus, setEcommerceStatus] = useState('');
  const [transferAction, setTransferAction] = useState('');
  const [description, setDescription] = useState('');

  const userId = getUserId();
  const userEmail = getUserEmail();
  const columnStateStorageKey = useMemo(() => {
    const id = (userId || '').trim();
    const email = (userEmail || '').trim();
    const key = id || email;
    return key ? `ffl-backoffice:ecommerce-status-mapping:ag-col-state:${key}` : null;
  }, [userId, userEmail]);

  const gridRef = useRef<AgGridReact<EcommerceStatusRow>>(null);

  useEffect(() => {
    dataSource.setLoadStateListener((s) => {
      setListLoading(s.loading);
    });
    return () => dataSource.setLoadStateListener(undefined);
  }, [dataSource]);

  useEffect(() => {
    dispatch(callToFetchEcommerceStatusList());
  }, [dispatch]);

  useEffect(() => {
    dataSource.setStoreId(effectiveStoreId || undefined);
    gridRef.current?.api?.refreshServerSide({ purge: true });
  }, [effectiveStoreId, dataSource]);

  const persistColumnState = useCallback(() => {
    if (!columnStateStorageKey) return;
    try {
      const api = gridRef.current?.api;
      if (!api) return;
      const state = api.getColumnState();
      localStorage.setItem(columnStateStorageKey, JSON.stringify(state));
    } catch {
      // ignore quota / private mode / parse issues
    }
  }, [columnStateStorageKey]);

  const restoreColumnState = useCallback(() => {
    if (!columnStateStorageKey) return;
    try {
      const api = gridRef.current?.api;
      if (!api) return;
      const raw = localStorage.getItem(columnStateStorageKey);
      if (!raw) return;
      const state = JSON.parse(raw) as ColumnState[];
      if (!Array.isArray(state) || state.length === 0) return;
      api.applyColumnState({ state, applyOrder: true });
    } catch {
      // ignore corrupt storage
    }
  }, [columnStateStorageKey]);

  const ecommerceStatusOptions = useMemo<StatusOption[]>(() => {
    const current = ecommerceStatus.trim();
    const byLower = new Map<string, StatusOption>();
    for (const raw of ecommerceStatusList) {
      const s = String(raw ?? '').trim();
      if (!s) continue;
      byLower.set(s.toLowerCase(), { label: toTitleCaseWords(s), value: s });
    }
    if (current) {
      const c = canonicalEcommerceStatus(current, ecommerceStatusList);
      byLower.set(c.toLowerCase(), { label: toTitleCaseWords(c), value: c });
    }
    return Array.from(byLower.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [ecommerceStatus, ecommerceStatusList]);

  const ecommerceStatusValue = useMemo<SingleValue<StatusOption>>(() => {
    if (!ecommerceStatus.trim()) return null;
    const c = canonicalEcommerceStatus(ecommerceStatus, ecommerceStatusList);
    return { label: toTitleCaseWords(c), value: c };
  }, [ecommerceStatus, ecommerceStatusList]);

  const transferActionOptionsForSelect = useMemo<StatusOption[]>(() => {
    const opts = [...TRANSFER_ACTION_OPTIONS];
    if (transferAction && !opts.some((o) => o.value === transferAction)) {
      opts.push({ label: actionDisplayLabel(transferAction), value: transferAction });
    }
    return opts;
  }, [transferAction]);

  const transferActionValue = useMemo<SingleValue<StatusOption>>(() => {
    if (!transferAction) return null;
    const found = transferActionOptionsForSelect.find((o) => o.value === transferAction);
    return found ?? null;
  }, [transferAction, transferActionOptionsForSelect]);

  const isAddMappingEnabled = useMemo(
    () => ecommerceStatus.trim().length > 0 && transferAction.trim().length > 0,
    [ecommerceStatus, transferAction]
  );

  const openAddModal = useCallback(() => {
    setModalMode('add');
    setEditingMappingId(null);
    editMappingIdRef.current = null;
    setEcommerceStatus('');
    setTransferAction('');
    setDescription('');
    saveSubmittedRef.current = false;
    saveRequestSawInProgressRef.current = false;
    dispatch(setEcommerceStatusMappingSaveError(null));
    onOpen();
  }, [dispatch, onOpen]);

  const findRowIndex = useCallback(
    (row: EcommerceStatusRow) =>
      dataSource.getAllRows().findIndex((r) => rowKey(r) === rowKey(row)),
    [dataSource, rowVersion]
  ); // rowVersion: re-bind after local edits so indices stay correct

  const openEditModal = useCallback(
    (row: EcommerceStatusRow) => {
      const rowData = row;
      const rawId = rowData?.id;
      const hasId = rawId != null && String(rawId).trim() !== '';
      setModalMode('edit');
      editMappingIdRef.current = hasId ? rawId : null;
      setEditingMappingId(hasId ? String(rawId) : null);
      setEcommerceStatus(canonicalEcommerceStatus(rowData.ecommerceStatus ?? '', ecommerceStatusList));
      setTransferAction(String(rowData.action ?? ''));
      setDescription(rowData.description ?? '');
      saveSubmittedRef.current = false;
      saveRequestSawInProgressRef.current = false;
      dispatch(setEcommerceStatusMappingSaveError(null));
      onOpen();
    },
    [dispatch, onOpen, ecommerceStatusList]
  );

  const openDeleteConfirmForRow = useCallback(
    (row: EcommerceStatusRow) => {
      const rawId = row?.id;
      deleteMappingIdRef.current =
        rawId != null && String(rawId).trim() !== '' ? rawId : null;
      deleteSubmittedRef.current = false;
      deleteRequestSawInProgressRef.current = false;
      dispatch(setEcommerceStatusMappingDeleteError(null));
      const rowIndex = findRowIndex(row);
      setDeleteRowIndex(rowIndex >= 0 ? rowIndex : null);
      onDeleteConfirmOpen();
    },
    [dispatch, findRowIndex, onDeleteConfirmOpen]
  );

  const onCloseDeleteConfirm = useCallback(() => {
    if (isDeletingEcommerceStatusMapping) {
      return;
    }
    setDeleteRowIndex(null);
    deleteMappingIdRef.current = null;
    deleteSubmittedRef.current = false;
    deleteRequestSawInProgressRef.current = false;
    dispatch(setEcommerceStatusMappingDeleteError(null));
    onDeleteConfirmClose();
  }, [dispatch, isDeletingEcommerceStatusMapping, onDeleteConfirmClose]);

  const onConfirmDelete = useCallback(() => {
    const id = deleteMappingIdRef.current;
    if (id == null || String(id).trim() === '' || isDeletingEcommerceStatusMapping) {
      return;
    }
    deleteSubmittedRef.current = true;
    deleteRequestSawInProgressRef.current = false;
    dispatch(
      callToDeleteEcommerceStatusMapping({
        id
      })
    );
  }, [dispatch, isDeletingEcommerceStatusMapping]);

  const rowPendingDelete = useMemo(() => {
    const all = dataSource.getAllRows();
    if (deleteRowIndex != null && deleteRowIndex >= 0 && deleteRowIndex < all.length) {
      return all[deleteRowIndex];
    }
    return null;
  }, [deleteRowIndex, dataSource, rowVersion]);

  const canDeleteMappingViaApi = useMemo(
    () => rowPendingDelete?.id != null && String(rowPendingDelete.id).trim() !== '',
    [rowPendingDelete]
  );

  const columnDefs = useMemo<ColDef<EcommerceStatusRow>[]>(
    () => [
      {
        colId: 'mappingRowId',
        field: 'id',
        headerName: 'ID',
        hide: true,
        suppressColumnsToolPanel: true,
        lockVisible: true,
        resizable: false,
        sortable: false,
        filter: false
      },
      {
        colId: 'ecommerceStatus',
        field: 'ecommerceStatus',
        headerName: 'E-Commerce Status',
        flex: 1.2,
        minWidth: 210,
        valueFormatter: ({ value }) => toTitleCaseWords(String(value ?? ''))
      },
      {
        field: 'action',
        headerName: 'Transfer Action',
        flex: 1,
        minWidth: 200,
        cellRenderer: ({ value }: { value: string }) => {
          const code = String(value ?? '');
          const actionStyle: TransferActionStyle = transferActionStyles[code] || {
            bg: '#EEF2FF',
            color: '#1D4ED8',
            alignItems: 'center'
          };
          return (
            <Box
              as="span"
              display="inline-flex"
              alignItems={actionStyle.alignItems}
              h="29px"
              px="12px"
              py="6px"
              borderRadius="4px"
              bg={actionStyle.bg}
              color={actionStyle.color}
              fontFamily="Lato, sans-serif"
              fontSize="14px"
              fontStyle="normal"
              fontWeight={600}
              lineHeight="17.01px"
            >
              {actionDisplayLabel(code)}
            </Box>
          );
        }
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 2,
        minWidth: 300,
        tooltipValueGetter: ({ value }) => {
          const text = String(value ?? '').trim();
          return text.length > 50 ? text : '';
        },
        cellRenderer: ({ value }: { value: string }) => {
          const descriptionText = String(value ?? '');
          return (
            <Box
              as="span"
              display="block"
              w="100%"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {descriptionText}
            </Box>
          );
        }
      },
      {
        colId: 'actions',
        headerName: 'Actions',
        minWidth: 120,
        maxWidth: 120,
        sortable: false,
        filter: false,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          height: '100%'
        },
        cellRenderer: (params: ICellRendererParams<EcommerceStatusRow>) => {
          const row = params.data ?? params.node?.data;
          return (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              gap="4px"
              w="100%"
              h="100%"
              minH="52px"
            >
              <button
                type="button"
                aria-label="Edit status mapping"
                style={actionIconButtonStyle}
                className="ecommerce-status-mapping__action-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  if (row != null) {
                    openEditModal(row);
                  }
                }}
              >
                <EditMappingIcon />
              </button>
              <button
                type="button"
                aria-label="Delete status mapping"
                style={actionIconButtonStyle}
                className="ecommerce-status-mapping__action-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  if (row != null) {
                    openDeleteConfirmForRow(row);
                  }
                }}
              >
                <DeleteMappingIcon />
              </button>
            </Box>
          );
        }
      }
    ],
    [openEditModal, openDeleteConfirmForRow]
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: false,
      resizable: false,
      filter: false
    }),
    []
  );

  const onCloseModal = useCallback(() => {
    setEcommerceStatus('');
    setTransferAction('');
    setDescription('');
    setModalMode('add');
    setEditingMappingId(null);
    editMappingIdRef.current = null;
    saveSubmittedRef.current = false;
    saveRequestSawInProgressRef.current = false;
    dispatch(setEcommerceStatusMappingSaveError(null));
    onClose();
  }, [dispatch, onClose]);

  const onSubmitMapping = useCallback(() => {
    if (!isAddMappingEnabled || isSavingEcommerceStatusMapping) return;
    saveSubmittedRef.current = true;
    saveRequestSawInProgressRef.current = false;
    const idForEdit =
      modalMode === 'edit'
        ? editMappingIdRef.current ?? (editingMappingId != null ? editingMappingId : null)
        : null;
    const hasEditId = idForEdit != null && String(idForEdit).trim() !== '';
    dispatch(
      callToSaveEcommerceStatusMapping({
        mode: modalMode,
        ecommerce_status: canonicalEcommerceStatus(ecommerceStatus.trim(), ecommerceStatusList),
        action: transferAction.trim(),
        description: description.trim(),
        ...(modalMode === 'edit' && hasEditId ? { id: idForEdit } : {})
      })
    );
  }, [
    isAddMappingEnabled,
    isSavingEcommerceStatusMapping,
    dispatch,
    modalMode,
    ecommerceStatus,
    transferAction,
    description,
    editingMappingId,
    ecommerceStatusList
  ]);

  useEffect(() => {
    if (isSavingEcommerceStatusMapping) {
      saveRequestSawInProgressRef.current = true;
      return;
    }
    if (!saveSubmittedRef.current || !saveRequestSawInProgressRef.current) {
      return;
    }
    saveSubmittedRef.current = false;
    saveRequestSawInProgressRef.current = false;
    if (ecommerceStatusMappingSaveError) {
      return;
    }
    dataSource.invalidateCache();
    setRowVersion((v) => v + 1);
    gridRef.current?.api?.refreshServerSide({ purge: true });
    onCloseModal();
  }, [
    isSavingEcommerceStatusMapping,
    ecommerceStatusMappingSaveError,
    dataSource,
    onCloseModal
  ]);

  useEffect(() => {
    if (isDeletingEcommerceStatusMapping) {
      deleteRequestSawInProgressRef.current = true;
      return;
    }
    if (!deleteSubmittedRef.current || !deleteRequestSawInProgressRef.current) {
      return;
    }
    deleteSubmittedRef.current = false;
    deleteRequestSawInProgressRef.current = false;
    if (ecommerceStatusMappingDeleteError) {
      return;
    }
    dataSource.invalidateCache();
    setRowVersion((v) => v + 1);
    gridRef.current?.api?.refreshServerSide({ purge: true });
    onCloseDeleteConfirm();
  }, [
    isDeletingEcommerceStatusMapping,
    ecommerceStatusMappingDeleteError,
    dataSource,
    onCloseDeleteConfirm
  ]);

  const gridOptions = useMemo<GridOptions<EcommerceStatusRow>>(
    () => ({
      rowModelType: 'serverSide',
      serverSideDatasource: dataSource,
      cacheBlockSize: ECOMMERCE_STATUS_MAPPING_CACHE_BLOCK_SIZE,
      maxBlocksInCache: 20,
      overlayNoRowsTemplate:
        '<span style="padding: 12px; color: #475467; font-size: 14px;">No status found</span>',
      animateRows: true,
      suppressCellFocus: true,
      suppressMovableColumns: false,
      suppressDragLeaveHidesColumns: true,
      domLayout: 'normal',
      getRowId: (p) => rowKey(p.data as EcommerceStatusRow),
      icons: agGridIconsConfig,
      columnDefs,
      defaultColDef,
      rowHeight: 52,
      headerHeight: 46,
      onGridReady: () => {
        restoreColumnState();
      },
      onColumnMoved: (e) => {
        if (e.finished === false) return;
        persistColumnState();
      }
    }),
    [dataSource, columnDefs, defaultColDef, restoreColumnState, persistColumnState]
  );

  return (
    <Box
      sx={{
        '& .ecommerce-status-mapping__action-btn': {
          border: 'none',
          outline: 'none',
          boxShadow: 'none',
          background: 'transparent'
        },
        '& .ecommerce-status-mapping__action-btn:hover, & .ecommerce-status-mapping__action-btn:focus, & .ecommerce-status-mapping__action-btn:focus-visible, & .ecommerce-status-mapping__action-btn:active': {
          border: 'none !important',
          outline: 'none !important',
          boxShadow: 'none !important',
          background: 'transparent'
        }
      }}
    >
      <Box
        display="flex"
        flexDirection={{ base: 'column', md: 'row' }}
        justifyContent={{ base: 'flex-start', md: 'space-between' }}
        alignItems={{ base: 'stretch', md: 'flex-start' }}
        gap={{ base: 3, md: 4 }}
        mb="14px"
      >
        <Box flex={{ md: 1 }} minW={0} pr={{ md: 2 }}>
          <Heading
            mb="4px"
            color="#0C1E46"
            fontFamily="Lato, sans-serif"
            fontSize="20px"
            fontStyle="normal"
            fontWeight={700}
            lineHeight="normal"
          >
            E-Commerce Status Mapping
          </Heading>
          <Text
            color="#666"
            fontFamily="Lato, sans-serif"
            fontSize="14px"
            fontStyle="normal"
            fontWeight={400}
            lineHeight="normal"
          >
            Map e-commerce order statuses to transfer actions. When a transfer receives an updated status, the system will automatically execute the configured action.
          </Text>
        </Box>
        <Button
          leftIcon={<AddIcon boxSize={2.5} />}
          colorScheme="blue"
          background="#0C1E46"
          borderRadius="6px"
          px="16px"
          py={{ base: 3, md: 2 }}
          fontSize="14px"
          w={{ base: '100%', md: 'auto' }}
          alignSelf={{ base: 'stretch', md: 'flex-start' }}
          flexShrink={0}
          whiteSpace={{ base: 'normal', md: 'nowrap' }}
          textAlign="center"
          onClick={openAddModal}
        >
          Add Status Mapping
        </Button>
      </Box>

      <Box
        border="1px solid #EAECF0"
        borderRadius="8px"
        overflow="hidden"
        position="relative"
        className="ag-theme-alpine ecommerce-status-mapping-ag-grid"
        style={{ width: '100%', height: 'min(60vh, 520px)' }}
        sx={{
          /**
           * App.scss sets `.ag-ltr .ag-header-viewport { display:flex; align-items:center }` which
           * can break header drag hit-testing. Reset vertical alignment for this grid only.
           */
          '& .ag-ltr .ag-header-viewport': {
            alignItems: 'stretch'
          },
          '& .ag-root-wrapper': {
            border: 'none'
          },
          '& .ag-header': {
            background: '#FCFCFD',
            borderBottom: '1px solid #EAECF0'
          },
          '& .ag-header-cell': {
            fontWeight: 700,
            color: '#101828'
          },
          '& .ag-row': {
            borderBottom: '1px solid #F2F4F7'
          },
          '& .ag-cell[col-id="ecommerceStatus"], & .ag-cell[col-id="description"]': {
            display: 'flex',
            alignItems: 'center'
          },
          '& .ag-cell[col-id="ecommerceStatus"] .ag-cell-wrapper, & .ag-cell[col-id="description"] .ag-cell-wrapper': {
            display: 'flex',
            alignItems: 'center',
            minHeight: '100%'
          },
          '& .ag-cell[col-id="ecommerceStatus"] .ag-cell-value, & .ag-cell[col-id="description"] .ag-cell-value': {
            display: 'flex',
            alignItems: 'center'
          },
          '& .ag-cell[col-id="ecommerceStatus"], & .ag-cell[col-id="ecommerceStatus"] .ag-cell-value': {
            color: '#000',
            fontFamily: 'Lato, sans-serif',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 600,
            lineHeight: '19.44px'
          },
          '& .ag-cell[col-id="description"], & .ag-cell[col-id="description"] .ag-cell-value': {
            color: '#666',
            fontFamily: 'Lato, sans-serif',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: '21px'
          },
          '& .ag-cell[col-id="actions"]': {
            display: 'flex',
            alignItems: 'center'
          },
          '& .ag-cell[col-id="actions"] .ag-cell-wrapper': {
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            width: '100%',
            minHeight: '100%'
          },
          '& .ag-cell[col-id="actions"] .ag-cell-value, & .ag-cell[col-id="actions"] .ag-cell-value-container': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            width: '100%',
            height: '100%'
          },
          '& .ag-header-cell[col-id="actions"]': {
            justifyContent: 'flex-start',
            textAlign: 'left'
          },
          '& .ag-header-cell[col-id="actions"] .ag-header-cell-label': {
            justifyContent: 'flex-start'
          },
          '& .ag-header-cell[col-id="actions"] .ag-header-cell-comp-wrapper': {
            justifyContent: 'flex-start'
          }
        }}
      >
        {listLoading && (
          <Box
            position="absolute"
            inset={0}
            zIndex={2}
            bg="rgba(255, 255, 255, 0.75)"
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="8px"
          >
            <Spinner thickness="3px" speed="0.65s" emptyColor="gray.200" color="#0C1E46" size="lg" />
          </Box>
        )}
        <AgGridReact<EcommerceStatusRow> ref={gridRef} gridOptions={gridOptions} />
      </Box>

      <Modal isOpen={isOpen} onClose={onCloseModal} isCentered size="2xl">
        <ModalOverlay
          background="rgba(0, 0, 0, 0.3)"
          className="modal-overlay"
          sx={{ backdropFilter: 'blur(4px)' }}
        />
        <ModalContent
          borderRadius="0.375rem"
          overflow="visible"
          maxW="640px"
          className="ecommerce-status-mapping-modal"
          sx={{
            '& .chakra-form-control': {
              paddingBottom: '0 !important'
            }
          }}
        >
          <IconButton
            aria-label="Close status mapping modal"
            icon={<CloseIcon boxSize={3.5} />}
            onClick={onCloseModal}
            size="sm"
            position="absolute"
            top="-14px"
            right="-14px"
            bg="#fff"
            border="1px solid #D0D5DD"
            borderRadius="999px"
            minW="30px"
            h="30px"
            zIndex={2}
            _hover={{ bg: '#fff' }}
          />
          <ModalHeader
            borderTopRadius="0.375rem"
            color="#FFF"
            textAlign="center"
            fontFamily="Lato, sans-serif"
            fontSize="16px"
            fontStyle="normal"
            fontWeight={700}
            lineHeight="normal"
            sx={{
              display: 'flex',
              height: '75.297px',
              padding: '30px',
              flexDirection: 'column',
              alignItems: 'center',
              alignSelf: 'stretch',
              borderBottom: '1px solid rgba(0, 0, 0, 0.15)',
              background: '#0C1E46'
            }}
          >
            {modalMode === 'edit' ? 'Edit Status Mapping' : 'Add Status Mapping'}
          </ModalHeader>
          <ModalBody px={{ base: 5, md: 8 }} pt={6} pb={4}>
            <Box display="flex" flexDirection="column" gap="24px">
            <FormControl isRequired mb={0}>
              <FormLabel
                color="#101828"
                fontFamily="Lato, sans-serif"
                fontSize="14px"
                fontWeight={700}
                lineHeight="normal"
                mb="10px"
              >
                E-Commerce Status
              </FormLabel>
              <CreatableSelect<StatusOption, false>
                inputId="ecommerce-status-mapping-status"
                aria-label="E-Commerce status"
                isClearable={modalMode === 'add'}
                isLoading={isLoadingEcommerceStatusList}
                options={ecommerceStatusOptions}
                value={ecommerceStatusValue}
                onChange={(opt) => setEcommerceStatus(opt?.value ?? '')}
                placeholder="Type or select a status"
                styles={mappingModalSelectStyles}
                formatCreateLabel={(input) => `Use "${input}"`}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                menuPosition="fixed"
                noOptionsMessage={({ inputValue }) =>
                  inputValue ? `Press Enter to use "${inputValue}"` : 'Type to add or select a status'
                }
              />
              {ecommerceStatusListLoadError ? (
                <Text mt={2} fontSize="13px" color="#B42318" fontFamily="Lato, sans-serif">
                  {ecommerceStatusListLoadError}
                </Text>
              ) : null}
            </FormControl>

            <FormControl isRequired mb={0}>
              <FormLabel
                color="#101828"
                fontFamily="Lato, sans-serif"
                fontSize="14px"
                fontWeight={700}
                lineHeight="normal"
                mb="10px"
              >
                Transfer Action
              </FormLabel>
              <Select<StatusOption, false>
                inputId="ecommerce-status-mapping-transfer-action"
                aria-label="Transfer action"
                options={transferActionOptionsForSelect}
                value={transferActionValue}
                onChange={(opt) => setTransferAction(opt?.value ?? '')}
                placeholder="Select Action"
                isSearchable
                isClearable={false}
                styles={mappingModalSelectStyles}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                menuPosition="fixed"
              />
            </FormControl>

            <FormControl mb={0}>
              <FormLabel
                color="#101828"
                fontFamily="Lato, sans-serif"
                fontSize="14px"
                fontWeight={700}
                lineHeight="normal"
                mb="10px"
              >
                Description
              </FormLabel>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter description for the status"
                minH="92px"
                resize="none"
                border="1px solid #D0D5DD"
                borderRadius="6px"
                fontFamily="Lato, sans-serif"
                fontSize="14px"
                _hover={{ borderColor: '#D0D5DD' }}
                _focusVisible={{ borderColor: '#98A2B3', boxShadow: 'none' }}
              />
            </FormControl>
            </Box>
          </ModalBody>
          <ModalFooter pb={6}>
            <Box w="100%" display="flex" justifyContent="flex-end" alignItems="center" gap={3}>
              <Button
                onClick={onCloseModal}
                variant="outline"
                border="1px solid rgba(0, 0, 0, 0.15)"
                color="#667085"
                bg="#fff"
                borderRadius="4px"
                fontFamily="Lato, sans-serif"
                fontWeight={700}
                sx={{
                  display: 'flex',
                  padding: '12px 23.781px 12px 24px',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
                _hover={{ bg: '#fff' }}
              >
                Cancel
              </Button>
              <Button
                onClick={onSubmitMapping}
                isDisabled={!isAddMappingEnabled || isSavingEcommerceStatusMapping}
                bg="#0C1E46"
                color="#fff"
                borderRadius="4px"
                opacity={isAddMappingEnabled && !isSavingEcommerceStatusMapping ? 1 : 0.5}
                minW={isSavingEcommerceStatusMapping ? '120px' : undefined}
                fontFamily="Lato, sans-serif"
                fontWeight={700}
                sx={{
                  display: 'flex',
                  padding: '12px 23.781px 12px 24px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  _disabled: {
                    opacity: 0.5,
                    bg: '#0C1E46',
                    color: '#fff',
                    cursor: 'not-allowed'
                  }
                }}
                _hover={
                  isAddMappingEnabled && !isSavingEcommerceStatusMapping
                    ? { bg: '#0A1939', opacity: 1 }
                    : { bg: '#0C1E46', opacity: 0.5 }
                }
              >
                {isSavingEcommerceStatusMapping ? (
                  <Spinner thickness="2px" speed="0.65s" emptyColor="gray.200" color="white" size="sm" />
                ) : modalMode === 'edit' ? (
                  'Save Mapping'
                ) : (
                  'Add Mapping'
                )}
              </Button>
            </Box>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isDeleteConfirmOpen}
        onClose={onCloseDeleteConfirm}
        closeOnOverlayClick={!isDeletingEcommerceStatusMapping}
        closeOnEsc={!isDeletingEcommerceStatusMapping}
        isCentered
        size="2xl"
      >
        <ModalOverlay
          background="rgba(0, 0, 0, 0.3)"
          className="modal-overlay"
          sx={{ backdropFilter: 'blur(4px)' }}
        />
        <ModalContent
          borderRadius="0.375rem"
          overflow="visible"
          maxW="640px"
          className="ecommerce-status-mapping-modal"
        >
          <IconButton
            aria-label="Close delete confirmation"
            icon={<CloseIcon boxSize={3.5} />}
            onClick={onCloseDeleteConfirm}
            isDisabled={isDeletingEcommerceStatusMapping}
            size="sm"
            position="absolute"
            top="-14px"
            right="-14px"
            bg="#fff"
            border="1px solid #D0D5DD"
            borderRadius="999px"
            minW="30px"
            h="30px"
            zIndex={2}
            _hover={{ bg: '#fff' }}
          />
          <ModalHeader
            borderTopRadius="0.375rem"
            color="#FFF"
            textAlign="center"
            fontFamily="Lato, sans-serif"
            fontSize="16px"
            fontStyle="normal"
            fontWeight={700}
            lineHeight="normal"
            sx={{
              display: 'flex',
              height: '75.297px',
              padding: '30px',
              flexDirection: 'column',
              alignItems: 'center',
              alignSelf: 'stretch',
              borderBottom: '1px solid rgba(0, 0, 0, 0.15)',
              background: '#0C1E46'
            }}
          >
            Delete Status Mapping
          </ModalHeader>
          <ModalBody px={{ base: 5, md: 8 }} pt={6} pb={6}>
            <Text
              color="#101828"
              fontFamily="Lato, sans-serif"
              fontSize="14px"
              fontWeight={400}
              lineHeight="21px"
            >
              Are you sure you want to delete this status mapping
              {rowPendingDelete?.ecommerceStatus ? (
                <>
                  {' '}
                  for <strong>{rowPendingDelete.ecommerceStatus}</strong>
                </>
              ) : null}
              ? This action cannot be undone.
            </Text>
          </ModalBody>
          <ModalFooter pb={6}>
            <Box w="100%" display="flex" justifyContent="flex-end" alignItems="center" gap={3}>
              <Button
                onClick={onCloseDeleteConfirm}
                isDisabled={isDeletingEcommerceStatusMapping}
                variant="outline"
                border="1px solid rgba(0, 0, 0, 0.15)"
                color="#667085"
                bg="#fff"
                borderRadius="4px"
                fontFamily="Lato, sans-serif"
                fontWeight={700}
                sx={{
                  display: 'flex',
                  padding: '12px 23.781px 12px 24px',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
                _hover={{ bg: '#fff' }}
              >
                No
              </Button>
              <Button
                onClick={onConfirmDelete}
                isDisabled={
                  isDeletingEcommerceStatusMapping || !canDeleteMappingViaApi
                }
                minW={isDeletingEcommerceStatusMapping ? '100px' : undefined}
                bg="#0C1E46"
                color="#fff"
                borderRadius="4px"
                opacity={canDeleteMappingViaApi ? 1 : 0.5}
                fontFamily="Lato, sans-serif"
                fontWeight={700}
                sx={{
                  display: 'flex',
                  padding: '12px 23.781px 12px 24px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  _disabled: {
                    opacity: 0.5,
                    bg: '#0C1E46',
                    color: '#fff',
                    cursor: 'not-allowed'
                  }
                }}
                _hover={
                  canDeleteMappingViaApi && !isDeletingEcommerceStatusMapping
                    ? { bg: '#0A1939', opacity: 1 }
                    : { bg: '#0C1E46', opacity: 0.5 }
                }
              >
                {isDeletingEcommerceStatusMapping ? (
                  <Spinner thickness="2px" speed="0.65s" emptyColor="gray.200" color="white" size="sm" />
                ) : (
                  'Yes'
                )}
              </Button>
            </Box>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
